// In a style file like src/styles/profile/form.style.ts

import { styled } from '../stitches.config'; // Or your equivalent css-in-js import

/**
 * A container for each row of information in display mode.
 * (e.g., "Full Name" ...... "John Doe")
 */
export const InfoRow = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  padding: '1rem 0',
  fontSize: '$sm',
  borderBottom: '1px solid slate', // Adds a subtle divider line
  gap: '1.5rem',            // <--- This is the "Buffer" between Label and Value
  
  // Remove border from the very last item so it looks clean
  '&:last-child': {
    borderBottom: 'none',
  },
});


export const KeyIconWrapper = styled('div', {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '1.5rem',
  height: '1.5rem',
  borderRadius: '4px',
  
  // These colors should match your GridTickCell variants
  variants: {
    color: {
      otherParty: {
        backgroundColor: '$blue4',
        color: '$blue11',
      },
      default: {
        backgroundColor: 'white',
        color: 'white',
      },
      match: {
        backgroundColor: '$green4',
        color: '$green11',
      },
      partial: {
        backgroundColor: '$yellow4',
        color: '$yellow11',
      },
      notTaught: {
        backgroundColor: '#ced4da',

      },
    }
  }
});

/**
 * The label for the information (e.g., "Full Name", "Email Address").
 * Typically has a slightly lighter color or less emphasis.
 */

// Add this new export
export const RecommendedBadge = styled('span', {
  display: 'inline-block',     // Allows padding/margins to work
  marginTop: '0.5rem',         // Pushes it slightly below the text
  padding: '2px 8px',          // Comfortable internal spacing
  fontSize: '0.5rem',          // "Very small"
  fontWeight: 600,
  textTransform: 'uppercase',  // Makes it look like a status label
  letterSpacing: '0.03em',
  
  // "Surround with Green" colors
  color: '#15803d',           // Dark readable green text
  backgroundColor: '#dcfce7', // Very light green background
  border: '1px solid #bbf7d0',// Subtle green border
  borderRadius: '4px',        // Rounded corners
  width: 'fit-content',       // Ensures the box is only as wide as the text
});

export const InfoLabel = styled('span', {
  fontSize: '$sm',
  fontWeight: 500,
  color: '$slate11',       // Slightly lighter/softer than the value
  minWidth: '130px',       // Ensures labels align perfectly vertically
  maxWidth: '75%',
  flexShrink: 0,           // Prevents label from getting squashed on small screens
});

export const InfoValue = styled('span', {
  fontSize: '$sm',
  color: '$slate12',
  textAlign: 'right',      // Right-aligns the values for a clean "ledger" look
  flex: 1,                 // Takes up all remaining space
  
  // The Wrapping Fixes
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  lineHeight: '1.5',       // Better readability for multi-line emails
});

// Styled Link Component for Files
export const FileLink = styled('a', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.4rem',
  padding: '0.25rem 0.75rem',
  borderRadius: '6px',
  backgroundColor: '$blue2', // Very light blue background
  color: '$blue11',          // Darker blue text
  fontSize: '$sm',
  textDecoration: 'none',
  fontWeight: 500,
  transition: 'background 0.2s',

  '&:hover': {
    backgroundColor: '$blue3',
    textDecoration: 'none',
  }
});

// export const InfoLabel = styled('span', { // Using 'span' as it's for display, not form input
//   color: '$slate11',
//   fontSize: '$sm',
//   fontWeight: 500,
// });

// /**
//  * The actual user data value (e.g., "John Doe").
//  * Typically has a darker, more prominent color.
//  */
// export const InfoValue = styled('span', {
//   color: '$slate12',
//   fontSize: '$sm',
//   whiteSpace: 'normal', 
//   overflowWrap: 'anywhere', 
//   wordBreak: 'break-word'
// });

export const SubjectsTable = styled('table', {
    width: '100%',
    borderCollapse: 'collapse', // Removes space between cell borders
    fontSize: '$sm', // Matches the font size of InfoRow
  });
  
  /**
   * The header cell for the subjects table (e.g., "Subject", "Levels").
   * It aligns with the InfoLabel's font weight for consistency.
   */
  export const Th = styled('th', {
    padding: '0.5rem 0.75rem',
    borderBottom: '1px solid $slate6', // Matches the InfoRow border
    textAlign: 'left',
    fontWeight: 500, // Matches the InfoLabel font weight
    color: '$slate11', // Matches the InfoLabel color
  });
  
  /**
   * The data cell for the subjects table (e.g., "Maths", "A-Level, GCSE").
   * It aligns with the InfoValue's color.
   */
  export const Td = styled('td', {
    padding: '0.75rem',
    textAlign: 'left',
    color: '$slate12', // Matches the InfoValue color
    // Remove the border from the last row in the table body for a cleaner look
    'tr:last-of-type > &': {
      borderBottom: 'none',
    },
  });

  // Add these to src/styles/profile/form.style.ts

/**
 * A simple horizontal line to create a visual break in a card.
 */
export const Separator = styled('hr', {
    border: 'none',
    height: '1px',
    backgroundColor: '$slate6',
    margin: '1.5rem 0',
  });
  
  /**
   * The CSS Grid layout for the subjects and levels.
   * This is now the main container with border-radius and overflow.
   */
  export const SubjectsGrid = styled('div', {
    display: 'grid',
    gap: '1px', // Creates grid lines against this container's background
    border: '1px solid $elementBorder', // Separator line
    borderRadius: '12px',
    background: '$slate3', // Light background visible as grid lines
    // Allow the grid to shrink inside flex/grid parents so its own scroller engages
    minWidth: 0,
    maxWidth: '100%',
    overflowX: 'auto',
    overflowY: 'hidden', // border-radius still clips children with overflow:auto
  });
  
  /**
   * A generic base for all cells in the grid.
   */
  const GridCellBase = styled('div', {
    display: 'grid',
    placeItems: 'center',
    padding: '0.75rem',
    backgroundColor: 'white',
    minHeight: '44px',
    fontSize: '$sm',
  });
  

  export const GridHeader = styled(GridCellBase, {
    fontWeight: 600,
    textAlign: 'center',
    fontSize: '$xs',
    // Style changes
    backgroundColor: '$blueWeeks',
    color: '$sand12',
  });
  

  export const GridSubjectLabel = styled(GridCellBase, {
    fontWeight: 500,
    textAlign: 'left',
    justifyContent: 'flex-start', // Aligns text to the left
    // Style changes
    backgroundColor: '$blueWeeks',
    color: '$sand12',
  });
  
  /**
   * A data cell in the grid, showing a tick or nothing.
   */
  export const GridTickCell = styled(GridCellBase, {
    variants: {
      ticked: {
        true: {
          backgroundColor: '$blue4',
          color: '$blue11',
        },
        false: {
          backgroundColor: 'white',
        },
        
      },
      isMatch: {
        true: {
          backgroundColor: '$green4',
          color: '$green11',      // Or your desired green checkmark color
        }
      }, 
      status: {
        exact: {
          backgroundColor: '$green4',
          color: '$green11',    
        }, 
        partial: {
          backgroundColor: '$yellow3',
          color: '$yellow11',
        },
        default: {
          backgroundColor: 'white',
          color: 'white',
        },
        notTaught: {
          backgroundColor: '#ced4da',

        },


        
        
      }
    },
    defaultVariants: {
      ticked: false,
    },
  });


  export const SubheaderContainer = styled('div', {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: '1rem', // Sets the space between the lines and the title
    margin: '2rem 0', // Adds vertical spacing above and below the sub-header
  });
  
  export const SubheaderTitle = styled('h3', {
    // Use a semantic heading for accessibility
    fontSize: '$sm',
    fontWeight: 500,
    color: '$slate11',
    // color: '$sand11', // A slightly muted, secondary text color from your theme
    whiteSpace: 'nowrap', // Ensures the title doesn't wrap
    margin: 0,
  });
  
  export const SubheaderLine = styled('div', {
    flexGrow: 1, // This is the magic part that makes the lines fill available space
    height: '1px',
    backgroundColor: '$separator', // Use your theme's separator color
  });


  // In /styles/profile/form.style.ts

// ... your existing styled-components

export const QualificationsGrid = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)', // Creates a two-column grid
  gap: '1.5rem',
  width: '100%',
  marginTop: '1.5rem',

  // On smaller screens, stack them in a single column
  '@media (max-width: 768px)': {
    gridTemplateColumns: '1fr',
  },
});

export const QualificationBox = styled('div', {
  border: '1px solid $elementBorder',
  borderRadius: '0.5rem',
  padding: '1.5rem',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '$subtleBackground',


  // This centers the last box if it's on its own row
  '&:last-child:nth-child(odd)': {
    gridColumn: 'span 2',
    '@media (min-width: 769px)': {
      margin: '0 auto', // Center it horizontally
      width: 'calc(50% - 0.75rem)', // Make it the same width as other columns
    },
  },
});

export const BoxHeader = styled('h4', {
  fontSize: '$md',
  fontWeight: 600,
  margin: '0 0 1.25rem 0', // Margin bottom to space from content
  color: '$sand12',
});

// For subject/grade lists
export const SubjectGradeRow = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  fontSize: '$sm',
  padding: '0.5rem 0',
  borderBottom: '1px solid $elementBackground',
  '&:last-child': {
    borderBottom: 'none',
  },
});

export const SubjectName = styled('span', {
  color: '$sand11',
});

export const SubjectGrade = styled('span', {
  fontWeight: '500',
  color: '$sand12',
});

// For single scores
export const ScoreDisplay = styled('div', {
  fontSize: '2.5rem',
  fontWeight: 700,
  color: '$blue11',
  lineHeight: 1.1,
});

export const MaxScoreDisplay = styled('span', {
  fontSize: '$base',
  fontWeight: 500,
  color: '$sand11',
  marginLeft: '0.5rem',
});


export const EditField = styled('div', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between', // Pushes label and input to opposite ends
  width: '100%',
  marginBottom: '1rem',
  gap: '1rem',

  // Make the input/dropdown take up the available space
  // '& > :last-child': {
  //   flexGrow: 1,
  // },
});


export const DescriptionText = styled('div', {
  padding: '0.25rem 0.5rem 1.5rem 0.5rem',
  lineHeight: 1.6,
  whiteSpace: 'pre-wrap',
  color: '#555',

  overflowWrap: 'break-word',
  wordBreak: 'break-word', // Optional, for older browser compatibility

  
  // Style for the fallback text
  '&.not-provided': {
    fontStyle: 'italic',
    color: '#999',
  },
});


export const DetailPageShell = styled('div', {
  background: '#f0f2f5',
  display: 'flex', 
  flexDirection: 'column',
  boxSizing: 'border-box',
  minHeight: 0,     
  overflowX: 'hidden', 
  overflowY: 'hidden',
  height: '100%', 
  width: '100%',
  borderRadius: 16,      // Round the outer shell

});

export const CardContent = styled('div', {
  padding: '1.5rem', // The padding that used to be on the card itself
  fontSize: '$sm',
  flexDirection: 'column',     
  display: 'flex', 
  minHeight: 0, // Ensure scroll works correctly inside a flexbox
  position: 'relative',
  flexGrow: 1, 
  flexShrink: 1, 
  background: 'white',
  overflowY: 'auto',
  overflowX: 'hidden',
  overscrollBehavior: 'none',
  
});

export const CardHeader = styled('div', {
  paddingLeft: '0.5rem',            // Horizontal padding for the title
  paddingRight: '0.5rem',
  // position: 'sticky',
  // top: 0,

  height: '3.5rem',               // "a couple of rem long"
  alignItems: 'center', // ✨ ADD THIS: To center the button vertically
  display: 'flex', // ✨ ADD THIS: To enable flexbox
  flexShrink: 0, 
  justifyContent: 'space-between', // ✨ ADD THIS: To push the button to the right
  borderBottom: '1px solid $elementBorder', // Separator line
  background: '$blueWeeks',
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
  fontSize: '$md',
  fontWeight: 600,
  color: '$sand12',
});


// 5. The button (previously BannerIconButton)
export const BannerIconButton = styled('button', { // changed to button for semantics
  all: 'unset', // Important to reset default button styles
  display: 'grid',
  placeItems: 'center',
  cursor: 'pointer',
  width: '2.25rem',
  height: '2.25rem',
  borderRadius: '50%',
  flexShrink: 0,
  marginLeft: '1rem', // Add some space between text and button
  marginRight: '1rem',
  color: '$sand11', // A neutral grey for the icon
  transition: 'background-color 0.2s ease, color 0.2s ease',
    // --- 2. Remove the color change from the hover state ---
    '&:not([data-active="true"]):hover': {
      backgroundColor: '$blueHover',
      // The 'color' property is removed, so it stays black
'& svg': {
      fill: 'var(--colors-blue11)',
    },  
    },
});


export const HeaderInfo = styled('div', {
  display: 'flex',
  flexDirection: 'column',

  flexGrow: 1,       // Allow it to take up the remaining space


  alignItems:'center',
  textAlign: 'center',


});

export const TruncatedTitle = styled('span', {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,     // Crucial for flex children to shrink
  flex: 1,         // Takes up all available space between the buttons
  textAlign: 'center', // Optional: keeps it centered if there is space
})